import { Invoice } from '../types/invoice';
import { BusinessData } from '../types/business';
import { Client } from '../types/client';
import { v4 as uuidv4 } from 'uuid';
import * as indexedDBService from '../utils/indexedDBService';
import { getTenantDataService } from './tenant-data-service';

/**
 * Service for managing invoices using IndexedDB
 */
export class InvoiceService {
  /**
   * Generate a unique invoice number based on business name and financial year
   * @param userId User ID
   * @param businessName Business name for generating prefix
   * @param customPrefix Optional custom prefix (e.g. 'QTN')
   * @returns Promise with the generated invoice number
   */
  public static async generateInvoiceNumber(userId: string, businessName: string, customPrefix: string = ''): Promise<string> {
    try {
      // Get the current financial year
      const today = new Date();
      const fiscalYearStart = new Date(today.getFullYear(), 3, 1); // April 1st
      let financialYear: string;

      // If current date is before April 1st, use previous year
      if (today < fiscalYearStart) {
        financialYear = `${today.getFullYear() - 1}-${today.getFullYear().toString().slice(-2)}`;
      } else {
        financialYear = `${today.getFullYear()}-${(today.getFullYear() + 1).toString().slice(-2)}`;
      }

      // Generate prefix from business name (first 3 letters) or use custom prefix
      let prefix = customPrefix;
      if (!prefix) {
        prefix = businessName ? businessName.substring(0, 3).toUpperCase() : 'INV';
      }

      if (!prefix || prefix.trim() === '') {
        prefix = 'INV';
      }

      // We don't have a dedicated counters store in the utils/indexedDBService yet effectively exposed,
      // but we can just count invoices for this year to get the next number, 
      // or implement a simple counter logic using checking existing invoices.
      // For improved reliability, we should ideally have a counters store.
      // Checking existing invoices:
      const allInvoices = await indexedDBService.getAllItems<Invoice>('invoices');
      const yearPrefix = `${prefix}/${financialYear}/`;

      let maxSerial = 0;
      allInvoices.forEach(inv => {
        if (inv.invoiceNumber && inv.invoiceNumber.startsWith(yearPrefix)) {
          const parts = inv.invoiceNumber.split('/');
          if (parts.length === 3) {
            const serial = parseInt(parts[2], 10);
            if (!isNaN(serial) && serial > maxSerial) {
              maxSerial = serial;
            }
          }
        }
      });

      const nextSerial = maxSerial + 1;
      const serialNumber = String(nextSerial).padStart(3, '0');

      // Format: PREFIX/YY-YY/001
      return `${prefix}/${financialYear}/${serialNumber}`;
    } catch (error) {
      console.error('Error generating invoice number:', error);
      return `${customPrefix || 'INV'}-${Date.now().toString().slice(-4)}`;
    }
  }

  /**
   * Create a new invoice in IndexedDB
   * @param invoice The invoice data to create
   * @param userId The user ID
   * @returns Promise with the created invoice
   */
  public static async createInvoice(invoice: Invoice, userId: string): Promise<Invoice> {
    try {
      // Ensure required fields are set
      if (!invoice.bankDetails) {
        invoice.bankDetails = {};
      }

      // Ensure status is valid
      if (!['draft', 'pending', 'paid', 'overdue', 'deleted'].includes(invoice.status)) {
        invoice.status = 'pending';
      }

      // Add metadata
      const invoiceWithMeta = {
        ...invoice,
        userId,
        createdAt: invoice.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        id: invoice.id || uuidv4()
      };

      await indexedDBService.putItem('invoices', invoiceWithMeta);

      // Auto-deduct inventory if invoice is created and not draft/deleted
      if (['pending', 'paid', 'overdue'].includes(invoiceWithMeta.status)) {
        if (invoiceWithMeta.items && invoiceWithMeta.items.length > 0) {
           try {
             const userProfile = await indexedDBService.getItemById<any>('user_profiles', userId);
             const tenantDb = getTenantDataService({ uid: userId } as any, userProfile);
             const allInventory = await tenantDb.getAll('inventory');
             
             for (const item of invoiceWithMeta.items) {
               const invId = item.inventoryItemId;
               if (invId) {
                  const invItem = allInventory.find((i: any) => i.id === invId);
                  if (invItem && (invItem.stockQuantity !== undefined || invItem.stock !== undefined)) {
                    // Update stock using stockQuantity (primary) or stock (fallback)
                    const currentStock = invItem.stockQuantity !== undefined ? invItem.stockQuantity : invItem.stock;
                    invItem.stockQuantity = Math.max(0, Number(currentStock || 0) - Number(item.quantity));
                    invItem.stock = invItem.stockQuantity; // Keep both in sync
                    invItem.updatedAt = new Date().toISOString();
                    await tenantDb.put('inventory', invItem);
                  }
               }
             }
           } catch(e) {
             console.error('Failed to update inventory', e);
           }
        }
      }

      // Automatically sync payment if created as Paid
      if (invoiceWithMeta.status === 'paid') {
        await this.syncInvoicePaymentToLedger(invoiceWithMeta, userId);
      }

      return invoiceWithMeta;
    } catch (error) {
      console.error('Error creating invoice:', error);
      throw new Error('Failed to create invoice: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  }

  /**
   * Update an existing invoice in IndexedDB
   * @param id Invoice ID
   * @param invoice Updated invoice data
   * @param userId User ID
   * @returns Promise with the updated invoice
   */
  public static async updateInvoice(id: string, invoice: Partial<Invoice>, userId: string): Promise<Invoice> {
    try {
      // Get the existing invoice
      const existingInvoice = await indexedDBService.getItemById<Invoice>('invoices', id);

      if (!existingInvoice) {
        throw new Error(`Invoice with ID ${id} not found`);
      }

      // Update invoice with new data
      const updatedInvoice = {
        ...existingInvoice,
        ...invoice,
        updatedAt: new Date().toISOString()
      };

      await indexedDBService.putItem('invoices', updatedInvoice);

      // Automatically sync payment if status changed to Paid
      if (updatedInvoice.status === 'paid' && existingInvoice.status !== 'paid') {
        await this.syncInvoicePaymentToLedger(updatedInvoice, userId);
      }

      return updatedInvoice;
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw new Error('Failed to update invoice');
    }
  }

  /**
   * Automatically generate a corresponding Inward entry in Cashbook or Bankbook when an invoice is marked as Paid
   */
  public static async syncInvoicePaymentToLedger(invoice: any, userId: string): Promise<void> {
    try {
      const invoiceId = invoice.id;
      const paymentMode = invoice.paymentInfo?.paymentMode || 'cash';

      if (paymentMode === 'bank') {
        // Check if bank transactions already has this linkedInvoiceId
        const bankTx = await indexedDBService.getAllItems<any>('bankTransactions');
        const exists = bankTx.some(t => t.linkedInvoiceId === invoiceId && t.userId === userId);
        
        if (!exists) {
          const bankAccount = invoice.paymentInfo?.bankAccount || {};
          const bankName = bankAccount.bank || 'Default Bank';
          const accountNumber = bankAccount.accountNumber || '';

          const bankTransactionData = {
            id: uuidv4(),
            userId,
            date: invoice.paymentInfo?.paymentDate || invoice.paymentDate || new Date().toISOString().split('T')[0],
            description: `Payment received for Invoice #${invoice.invoiceNumber}`,
            type: 'credit', // Inward
            amount: Number(invoice.amount) || 0,
            category: 'Sales',
            reference: invoice.paymentInfo?.paymentReference || '',
            bank: bankName,
            accountNumber: accountNumber,
            chequeNumber: invoice.paymentInfo?.paymentReference || '',
            linkedEntityType: 'client',
            linkedEntityId: invoice.clientId || '',
            linkedEntityName: invoice.client || '',
            linkedInvoiceId: invoiceId,
            createdAt: Date.now()
          };

          await indexedDBService.putItem('bankTransactions', bankTransactionData);
        }
      } else {
        // Check if cash transactions already has this linkedInvoiceId
        const cashTx = await indexedDBService.getAllItems<any>('cashTransactions');
        const exists = cashTx.some(t => t.linkedInvoiceId === invoiceId && t.userId === userId);

        if (!exists) {
          const cashTransactionData = {
            id: uuidv4(),
            userId,
            date: invoice.paymentInfo?.paymentDate || invoice.paymentDate || new Date().toISOString().split('T')[0],
            description: `Payment received for Invoice #${invoice.invoiceNumber}`,
            type: 'credit', // Inward
            amount: Number(invoice.amount) || 0,
            category: 'Sales',
            reference: invoice.paymentInfo?.paymentReference || '',
            linkedEntityType: 'client',
            linkedEntityId: invoice.clientId || '',
            linkedEntityName: invoice.client || '',
            linkedInvoiceId: invoiceId,
            createdAt: Date.now()
          };

          await indexedDBService.putItem('cashTransactions', cashTransactionData);
        }
      }
    } catch (error) {
      console.error('Error syncing invoice payment to ledger:', error);
    }
  }

  /**
   * Delete an invoice from IndexedDB (soft delete)
   * @param id Invoice ID
   * @param userId User ID
   * @returns Promise that resolves when the invoice is deleted
   */
  public static async deleteInvoice(id: string, userId: string): Promise<void> {
    try {
      // Soft delete by marking status as deleted
      const existingInvoice = await indexedDBService.getItemById<Invoice>('invoices', id);
      if (existingInvoice) {
        const updatedInvoice = {
          ...existingInvoice,
          status: 'deleted' as const, // Cast to literal type
          updatedAt: new Date().toISOString()
        };
        await indexedDBService.putItem('invoices', updatedInvoice);
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw new Error('Failed to delete invoice');
    }
  }

  /**
   * Get an invoice by ID from IndexedDB
   * @param id Invoice ID
   * @param userId User ID
   * @returns Promise with the invoice or null if not found
   */
  public static async getInvoice(id: string, userId: string): Promise<Invoice | null> {
    try {
      const invoice = await indexedDBService.getItemById<Invoice>('invoices', id);

      if (!invoice) {
        return null;
      }

      // Check if invoice is not deleted
      if (invoice.status !== 'deleted') {
        return invoice;
      }

      return null;
    } catch (error) {
      console.error('Error getting invoice:', error);
      throw new Error('Failed to get invoice');
    }
  }

  /**
   * Get all invoices for a user from IndexedDB
   * @param userId User ID
   * @returns Promise with an array of invoices
   */
  public static async getInvoices(userId: string): Promise<Invoice[]> {
    try {
      const invoices = await indexedDBService.getAllItems<Invoice>('invoices');

      return invoices.filter(invoice =>
        (invoice.userId === userId || !invoice.userId) && // Handle legacy data potentially missing userId
        invoice.status !== 'deleted'
      );
    } catch (error) {
      console.error('Error getting invoices:', error);
      throw new Error('Failed to get invoices');
    }
  }

  /**
   * Fetch business details from IndexedDB
   * @param userId User ID
   * @returns Promise with the business details or null if not found
   */
  public static async fetchBusinessDetails(userId: string): Promise<BusinessData | null> {
    try {
      // Try to get by ID first (assuming ID matches userId)
      let businessData = await indexedDBService.getItemById<any>('businesses', userId);

      if (!businessData) {
        // If not found by ID, try scanning all (fallback)
        const allBusinesses = await indexedDBService.getAllItems<any>('businesses');
        if (allBusinesses.length > 0) {
          businessData = allBusinesses[0];
        }
      }

      if (businessData) {
        return {
          id: businessData.id,
          name: businessData.name || businessData.businessName || '',
          businessName: businessData.businessName || businessData.name || '',
          gstin: businessData.gstin || businessData.GSTIN || '',
          address: businessData.address || '',
          city: businessData.city || '',
          state: businessData.state || '',
          pincode: businessData.pincode || '',
          email: businessData.email || '',
          phone: businessData.phone || '',
          website: businessData.website || '',
          bankDetails: businessData.bankDetails || {},
        };
      }

      return null;
    } catch (error) {
      console.error('Error fetching business details:', error);
      return null;
    }
  }

  /**
   * Fetch clients from IndexedDB
   * @param userId User ID
   * @returns Promise with an array of clients
   */
  public static async fetchClients(userId: string): Promise<Client[]> {
    try {
      const clients = await indexedDBService.getAllItems<any>('clients');
      return clients.map(client => ({
        ...client,
        id: client.id
      })) as Client[];
    } catch (error) {
      console.error('Error fetching clients from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Fetch inventory items from IndexedDB
   * @param userId User ID
   * @returns Promise with an array of inventory items
   */
  public static async fetchInventoryItems(userId: string): Promise<any[]> {
    try {
      const items = await indexedDBService.getAllItems<any>('inventory');

      // Normalize item data structure
      return items.map(data => ({
        id: data.id,
        name: data.name || data.productName || 'Unnamed Item',
        description: data.description || data.details || '',
        // Support various price field names
        rate: data.sellingPrice || data.rate || data.price || 0,
        sellingPrice: data.sellingPrice || data.rate || data.price || 0,
        purchasePrice: data.purchasePrice || data.costPrice || 0,
        // Handle GST related fields
        hsnCode: data.hsnCode || data.hsn || '',
        gstRate: data.gstRate || data.taxRate || 18,
        // Stock related fields
        stockQuantity: data.stockQuantity || data.stock || data.quantity || 0,
        stock: data.stock || data.stockQuantity || data.quantity || 0,
        // Category and unit
        category: data.category || 'Uncategorized',
        unit: data.unit || 'Nos',
        // Add any other fields from the original data
        ...data
      }));
    } catch (error) {
      console.error('Error fetching inventory items from IndexedDB:', error);
      return [];
    }
  }

  /**
   * Save invoice as draft to IndexedDB
   * @param invoiceData The invoice data to save as draft
   * @param userId The user ID
   * @returns Promise with the saved draft
   */
  public static async saveDraft(invoiceData: any, userId: string): Promise<any> {
    try {
      const draftId = invoiceData.draftId || `draft_${Date.now()}_${uuidv4()}`;

      const draft = {
        ...invoiceData,
        draftId,
        userId,
        status: 'draft' as const,
        savedAt: new Date().toISOString(),
        isDraft: true
      };

      // Save to a dedicated drafts store or use invoices store with draft status
      await indexedDBService.putItem('invoices', draft);

      return draft;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw new Error('Failed to save draft');
    }
  }

  /**
   * Load a specific draft from IndexedDB
   * @param draftId The draft ID
   * @param userId The user ID
   * @returns Promise with the draft or null if not found
   */
  public static async loadDraft(draftId: string, userId: string): Promise<any | null> {
    try {
      const draft = await indexedDBService.getItemById<any>('invoices', draftId);

      if (draft && draft.status === 'draft' && draft.userId === userId) {
        return draft;
      }

      return null;
    } catch (error) {
      console.error('Error loading draft:', error);
      return null;
    }
  }

  /**
   * Load all drafts for a user from IndexedDB
   * @param userId The user ID
   * @returns Promise with an array of drafts
   */
  public static async loadAllDrafts(userId: string): Promise<any[]> {
    try {
      const allInvoices = await indexedDBService.getAllItems<any>('invoices');

      return allInvoices.filter(invoice =>
        invoice.status === 'draft' &&
        invoice.userId === userId &&
        invoice.isDraft === true
      ).sort((a, b) => {
        // Sort by savedAt date, newest first
        const dateA = new Date(a.savedAt || a.createdAt || 0).getTime();
        const dateB = new Date(b.savedAt || b.createdAt || 0).getTime();
        return dateB - dateA;
      });
    } catch (error) {
      console.error('Error loading drafts:', error);
      return [];
    }
  }

  /**
   * Delete a draft from IndexedDB
   * @param draftId The draft ID
   * @param userId The user ID
   * @returns Promise that resolves when the draft is deleted
   */
  public static async deleteDraft(draftId: string, userId: string): Promise<void> {
    try {
      const draft = await indexedDBService.getItemById<any>('invoices', draftId);

      if (draft && draft.status === 'draft' && draft.userId === userId) {
        await indexedDBService.deleteItem('invoices', draftId);
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      throw new Error('Failed to delete draft');
    }
  }

  /**
   * Duplicate an existing invoice
   * @param invoiceId The invoice ID to duplicate
   * @param userId The user ID
   * @returns Promise with the duplicated invoice
   */
  public static async duplicateInvoice(invoiceId: string, userId: string): Promise<Invoice> {
    try {
      const originalInvoice = await this.getInvoice(invoiceId, userId);

      if (!originalInvoice) {
        throw new Error('Invoice not found');
      }

      // Generate new invoice number
      const businessDetails = await this.fetchBusinessDetails(userId);
      const businessName = businessDetails?.name || businessDetails?.businessName || '';
      const newInvoiceNumber = await this.generateInvoiceNumber(userId, businessName);

      // Create duplicate with new ID and invoice number
      const duplicateInvoice: Invoice = {
        ...originalInvoice,
        id: uuidv4(),
        invoiceNumber: newInvoiceNumber,
        date: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        status: 'draft',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await indexedDBService.putItem('invoices', duplicateInvoice);

      return duplicateInvoice;
    } catch (error) {
      console.error('Error duplicating invoice:', error);
      throw new Error('Failed to duplicate invoice');
    }
  }

  /**
   * Clean up old drafts (older than specified days)
   * @param userId The user ID
   * @param daysOld Number of days after which drafts should be deleted (default: 30)
   * @returns Promise with the number of drafts deleted
   */
  public static async cleanupOldDrafts(userId: string, daysOld: number = 30): Promise<number> {
    try {
      const drafts = await this.loadAllDrafts(userId);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysOld);

      let deletedCount = 0;

      for (const draft of drafts) {
        const draftDate = new Date(draft.savedAt || draft.createdAt || 0);
        if (draftDate < cutoffDate) {
          await this.deleteDraft(draft.draftId || draft.id, userId);
          deletedCount++;
        }
      }

      return deletedCount;
    } catch (error) {
      console.error('Error cleaning up old drafts:', error);
      return 0;
    }
  }
}

export default InvoiceService; 
